import { api } from '@/lib/api';
import { uploadFileWithProgress } from '@/features/vessel-storage/service';
import type {
  PartDocument,
  PartDocumentDownloadUrl,
  PartDocumentKind,
  PartPhotoUrl,
  PresignedUpload,
} from './types';

/**
 * Parts storage REST client (L2 backend contract). Mirrors the
 * vessel-storage service structure end-to-end.
 *
 * The XHR upload helper itself lives in `vessel-storage/service`
 * and is shared as-is — it's a generic XHR PUT, no entity coupling.
 */
export const partStorageApi = {
  // ---------- Photo ----------
  presignPhotoUpload: async (
    partId: string,
    input: { contentType: string; contentLength: number; fileName?: string },
  ): Promise<PresignedUpload> => {
    const { data } = await api.post<PresignedUpload>(
      `/parts/${partId}/photo/presign-upload`,
      input,
    );
    return data;
  },

  confirmPhotoUpload: async (
    partId: string,
    input: { fileKey: string; contentType: string; sizeBytes: number },
  ): Promise<{ photoKey: string }> => {
    const { data } = await api.post<{ photoKey: string }>(
      `/parts/${partId}/photo/confirm-upload`,
      input,
    );
    return data;
  },

  getPhotoUrl: async (partId: string): Promise<PartPhotoUrl | null> => {
    const { data } = await api.get<PartPhotoUrl | null>(
      `/parts/${partId}/photo/url`,
    );
    return data;
  },

  deletePhoto: async (partId: string): Promise<void> => {
    await api.delete(`/parts/${partId}/photo`);
  },

  // ---------- Documents ----------
  presignDocumentUpload: async (
    partId: string,
    input: { contentType: string; contentLength: number; fileName: string },
  ): Promise<PresignedUpload> => {
    const { data } = await api.post<PresignedUpload>(
      `/parts/${partId}/documents/presign-upload`,
      input,
    );
    return data;
  },

  createDocument: async (
    partId: string,
    input: {
      kind: PartDocumentKind;
      fileKey: string;
      fileName: string;
      contentType: string;
      sizeBytes: number;
    },
  ): Promise<PartDocument> => {
    const { data } = await api.post<PartDocument>(
      `/parts/${partId}/documents`,
      input,
    );
    return data;
  },

  listDocuments: async (
    partId: string,
    filter?: { kind?: PartDocumentKind },
  ): Promise<PartDocument[]> => {
    const { data } = await api.get<PartDocument[]>(
      `/parts/${partId}/documents`,
      { params: filter?.kind ? { kind: filter.kind } : undefined },
    );
    return data;
  },

  getDocumentDownloadUrl: async (
    partId: string,
    docId: string,
  ): Promise<PartDocumentDownloadUrl> => {
    const { data } = await api.get<PartDocumentDownloadUrl>(
      `/parts/${partId}/documents/${docId}/download-url`,
    );
    return data;
  },

  deleteDocument: async (partId: string, docId: string): Promise<void> => {
    await api.delete(`/parts/${partId}/documents/${docId}`);
  },
};

/** Re-export so callers don't need to reach into vessel-storage. */
export { uploadFileWithProgress };
